import React, { useEffect, useState } from 'react';
import Modal from 'components/Tvendo/Common/ModalBase';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';
import {
  Divider,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Switch,
} from '@material-ui/core';
import { wsGet, wsUpdate } from 'services/WService';

const TOMADOR = 'Tomador de pedidos';
const ENTREGAS = 'Entregas';

// apps: en qué aplicaciones aparece (sin apps, en todas).
// alIniciarSesion: aplicaciones que leen el valor recién en el próximo login.
const GRUPOS = [
  {
    titulo: 'Validaciones',
    opciones: [
      {
        campo: 'validate_version',
        titulo: 'Exigir la última versión',
        texto: 'No deja iniciar sesión si el aplicativo no está actualizado.',
      },
      {
        campo: 'false_gps',
        titulo: 'Restringir GPS falso',
        texto: 'Bloquea al usuario si usa una aplicación que simula la ubicación. Solo actúa si la restricción general está activa.',
      },
      {
        campo: 'validate_distace',
        titulo: 'Validar distancia al cliente',
        texto: 'Exige estar cerca del cliente para tomar el pedido.',
        apps: [TOMADOR],
        alIniciarSesion: [TOMADOR],
      },
      {
        campo: 'validate_distace',
        titulo: 'Validar distancia general',
        texto: 'Si el responsable es el vendedor, valida con la distancia de variables. Apaga la de ruta.',
        apps: [ENTREGAS],
      },
      {
        campo: 'route_distance',
        titulo: 'Validar distancia de la ruta',
        texto: 'Si el responsable es el vendedor, valida con la distancia de la ruta. Apaga la general.',
        apps: [ENTREGAS],
      },
      {
        campo: 'optimized_gps',
        titulo: 'GPS de mayor precisión',
        texto: 'El celular toma la ubicación con más precisión.',
        apps: [TOMADOR],
        alIniciarSesion: [TOMADOR],
      },
      {
        campo: 'validate_furniture_distance',
        titulo: 'Validar distancia en la foto del mueble',
        texto: 'Exige estar cerca del cliente al tomar la foto del mueble.',
        apps: [TOMADOR],
      },
      {
        campo: 'edit_furniture_photo',
        titulo: 'Corregir la foto del mueble',
        texto: 'Permite volver a tomar la foto el mismo día; la nueva reemplaza a la anterior.',
        apps: [TOMADOR],
      },
    ],
  },
  {
    titulo: 'Pedido',
    opciones: [
      {
        campo: 'must_have_products',
        titulo: 'Cargar productos infaltables',
        texto: 'Al abrir un pedido nuevo agrega los infaltables de su mesa que el vendedor aún no completa en el mes.',
        apps: [TOMADOR],
      },
    ],
  },
  {
    titulo: 'Permisos solo por hoy',
    nota: 'Se apagan solos al terminar el día.',
    opciones: [
      {
        campo: 'remote_order_auth',
        titulo: 'Digitar fuera de ruta',
        texto: 'Permite tomar pedidos fuera de su ruta y sin validar el GPS.',
        apps: [TOMADOR],
      },
      {
        campo: 'early_close_auth',
        titulo: 'Cerrar la operación antes de hora',
        texto: 'Permite cerrar la operación antes de la hora de cierre.',
        apps: [TOMADOR],
      },
    ],
  },
];

/**
 * Toggles de las aplicaciones móviles de un usuario, con un texto para cada uno.
 * model: { userId, userName, appId? }. Sin appId muestra todas sus aplicaciones.
 */
function UserAppSettingsModal(props) {
  const { model, setOpen, showNotify } = props;
  const [apps, setApps] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!model.open || !model.userId) return;
    cargar();
  }, [model.open, model.userId]);

  async function cargar() {
    setApps([]);
    setCargando(true);
    const res = await wsGet(`oauth/user/${model.userId}/app`);
    setCargando(false);
    if (res.success) setApps(res.data);
  }

  async function cambiar(app, campo, checked) {
    const valor = checked ? '1' : '0';
    setGuardando(true);
    // cada aplicación lleva sus propias validaciones: hay que decir sobre cuál escribir
    const res = await wsUpdate(`movil/update/${model.userId}/validation`, {
      [campo]: valor,
      oauth_app_id: app.oauth_app_id ?? app.app?.id,
    });
    setGuardando(false);
    if (res.success) {
      showNotify(res.message);
      // las dos distancias se excluyen en el ws: se toman de la respuesta para ver apagada la otra
      setApps((lista) =>
        lista.map((a) =>
          a.id === app.id
            ? {
                ...a,
                [campo]: valor,
                validate_distace: res.data.validate_distace,
                route_distance: res.data.route_distance,
              }
            : a,
        ),
      );
    } else {
      showNotify(res.message, 'error');
    }
  }

  function texto(opcion, aplicacion) {
    if (opcion.alIniciarSesion?.includes(aplicacion)) {
      return `${opcion.texto} Se aplica cuando el usuario vuelve a iniciar sesión.`;
    }
    return opcion.texto;
  }

  const visibles = apps.filter((a) => !model.appId || a.id === model.appId);

  return (
    <Modal
      title={(<>Configuración: {model.userName} {cargando && <LoadingSpinner />}</>)}
      model={model}
      setOpen={setOpen}
      hideSave
      fullWidth
      maxWidth="sm"
    >
      {!cargando && visibles.length === 0 && (
        <p style={{ margin: 16 }}>El usuario no tiene aplicaciones asignadas.</p>
      )}

      {visibles.map((app) => {
        const aplicacion = app.app?.description;

        return (
          <div key={app.id}>
            <h5 style={{ margin: '12px 16px 0', fontWeight: 'bold' }}>{aplicacion}</h5>

            {GRUPOS.map((grupo) => {
              const opciones = grupo.opciones.filter((o) => !o.apps || o.apps.includes(aplicacion));
              if (opciones.length === 0) return null;

              return (
                <List
                  key={grupo.titulo}
                  dense
                  subheader={
                    <ListSubheader disableSticky>
                      {grupo.titulo}
                      {grupo.nota && <span style={{ fontWeight: 'normal' }}> · {grupo.nota}</span>}
                    </ListSubheader>
                  }
                >
                  {opciones.map((opcion, i) => (
                    <React.Fragment key={opcion.campo}>
                      {i > 0 && <Divider component="li" />}
                      <ListItem style={{ paddingRight: 80 }}>
                        <ListItemText primary={opcion.titulo} secondary={texto(opcion, aplicacion)} />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={app[opcion.campo] === '1'}
                            disabled={guardando}
                            onChange={(e) => cambiar(app, opcion.campo, e.target.checked)}
                            color="secondary"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              );
            })}
          </div>
        );
      })}
    </Modal>
  );
}

export default UserAppSettingsModal;
